import {
  useState,
  useCallback,
} from 'react';

import { InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from "@azure/msal-react";

/**
 * Custom hook to call a web API using bearer token obtained from Microsoft Authentication Library
 */
const useFetchWithMsal = () => {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const { result, error: msalError } = useMsalAuthentication(InteractionType.Redirect, {
    account: instance.getActiveAccount(),
    redirectUri: window.location.pathname.replace(/\/+$/, ''),
    scopes: ["176b2c1e-24a8-4f79-ad71-5aff1fccf1ec/.default"]
  });

  /**
     * Execute a fetch request with the given options
     * @param {string} method: GET, POST, PUT, DELETE
     * @param {String} endpoint: The endpoint to call
     * @param {Object} data: The data to send to the endpoint, if any
     * @returns JSON response
     */
  const execute = async (method, endpoint, data = null) => {
    if (msalError) {
      setError(msalError);
      return;
    }

    if (result) {
      try {
        let response = null;

        const headers = new Headers();
        const bearer = `Bearer ${result.accessToken}`;
        headers.append("Authorization", bearer);

        if (data) headers.append('Content-Type', 'application/json');

        let options = {
          method: method,
          headers: headers,
          body: data ? JSON.stringify(data) : null,
        };

        setIsLoading(true);

        try{
          response = await (await fetch(endpoint, options)).json();
        } catch (e) {
          //Had to do this, because the Put of event-schemas return a non-json object and failed the json conversion
          //tried to have a more generic implementation, where it could try to do in a two step, first the response and then the .json()
          // but it failed to due react bad state. I have not investigated too  much how to solve this.
          response = await (await fetch(endpoint, options));
        }
        setData(response);

        setIsLoading(false);
        return response;
      } catch (e) {
        setError(e);
        setIsLoading(false);
        throw e;
      }
    }
  };

  return {
    isLoading,
    error,
    data,
    execute: useCallback(execute, [result, msalError]), // to avoid infinite calls when inside a `useEffect`
  };
};

export default useFetchWithMsal;